import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card } from 'reactstrap';
import { Button } from 'primereact/button';
import { isEmpty } from 'lodash';
import PulseLoader from 'react-spinners/PulseLoader';
import { DataTable } from 'primereact/datatable';
import { Tag } from 'primereact/tag';
import { Column } from 'primereact/column';
import { useHistory } from 'react-router-dom';

import firebase from '../../../config/firebase.config';
import { teamActionTypes } from '@store/Team/Team.actionTypes';
import { useTeamMemberDetailsForSelect } from '../../../hooks/useTeamMemberDetailsForSelect';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { InputComponent } from '@/components/Input/Input.component';
import { ButtonComponent } from '@/components/Button/Button.component';
import { buttonVariants } from '@/components/Button';
import { MultiSelectDropdown } from '@/components/MultiSelectDropdown';
import { appRoutes } from '@/routes/routes';
import styles from '../Tasks.module.scss';
// These CSS files style the PrimeReact DataTable and date picker used below
import 'react-datepicker/dist/react-datepicker.css';
import '../task-form.css';

// Kept outside the component — uses React hooks so it must only be called
// at the top level of a component render. Works correctly here because the
// caller (ProdaffTasksTab) calls it unconditionally at the top level.
const useSortableList = (taskLists, config = null) => {
  const [sortConfig, setSortConfig] = useState(config);

  const sortedItems = useMemo(() => {
    let sortableItems = [...taskLists];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key])
          return sortConfig.direction === 'ascending' ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key])
          return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [taskLists, sortConfig]);

  const requestSort = (key) => {
    const direction =
      sortConfig?.key === key && sortConfig.direction === 'ascending'
        ? 'descending'
        : 'ascending';
    setSortConfig({ key, direction });
  };

  return { items: sortedItems, requestSort, sortConfig };
};

const STATUS_OPTIONS = [
  { value: 'TO DO', label: 'TO DO' },
  { value: 'IN PROGRESS', label: 'IN PROGRESS' },
  { value: 'QA', label: 'QA' },
  { value: 'DONE', label: 'DONE' },
];

const PRIORITY_OPTIONS = [
  { value: 'P1', label: 'P1' },
  { value: 'P2', label: 'P2' },
  { value: 'P3', label: 'P3' },
  { value: 'P4', label: 'P4' },
];

const STATUS_SEVERITY = {
  DONE: 'success',
  'IN PROGRESS': 'info',
  'TO DO': 'danger',
  QA: 'warning',
};

const database = firebase.firestore();

const ProdaffTasksTab = () => {
  const teams = useSelector((state) => ({
    loadingProject: state.team.loadingProject,
    activeTeam: state.team.activeTeam,
    selectedTeamId: state.team.selectedTeamID,
    projects: state.team.projects,
    active: state.team.active,
  }));

  const { activeCompany } = useSelector((state) => state.auth.profile);
  const dispatch = useDispatch();
  const history = useHistory();

  const activeTeamId =
    teams.selectedTeamId ?? teams.active?.activeTeamid ?? null;
  const { memberDetails, isLoading: membersLoading } =
    useTeamMemberDetailsForSelect(activeTeamId);

  const members = useMemo(() => {
    if (!memberDetails || typeof memberDetails !== 'object') return [];
    return Object.entries(memberDetails)
      .map(([uid, m]) => ({
        userUID: uid,
        userName: m?.userName || m?.userEmailid || uid,
      }))
      .filter((m) => m.userUID)
      .sort((a, b) => (a.userName || '').localeCompare(b.userName || ''));
  }, [memberDetails]);

  const [taskLists, setTaskLists] = useState([]);
  const [initialTaskLists, setInitialTaskLists] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [priorityFilter, setPriorityFilter] = useState([]);
  const [dataStatus, setDataStatus] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);

  const isInitialDataLoaded =
    !teams.loadingProject &&
    !membersLoading &&
    (!loading || projects.length === 0);

  useEffect(() => {
    if (!isEmpty(teams.active)) {
      dispatch({
        type: teamActionTypes.GET_PROJECT,
        payload: { companyId: activeCompany.companyId },
      });
    }
  }, [teams.selectedTeamId]);

  useEffect(() => {
    if (JSON.stringify(teams.projects) !== '{}' && members?.length) {
      const availableProjects = Array.isArray(teams.projects)
        ? teams.projects
            .filter((p) => !p?.data?.deleted)
            .map((p) => ({ id: p.id, data: p.data }))
        : [];

      setProjects(availableProjects);

      if (
        availableProjects.length > 0 &&
        members?.length &&
        selectedProjects.length === 0
      ) {
        const first = availableProjects[0];
        setSelectedProjects([
          { value: first.id, label: first.data.project_name },
        ]);
        fetchTasksFromProjects([first.id]);
      }
    }
  }, [members, teams.projects]);

  const { items, sortConfig } = useSortableList(taskLists);

  const refreshDataTable = async () => {
    const projectIds =
      selectedProjects.length > 0
        ? selectedProjects.map((p) => p.value)
        : projects.length > 0
          ? [projects[0].id]
          : [];

    if (projectIds.length > 0) await fetchTasksFromProjects(projectIds);
  };

  const assigneeBodyTemplate = (rowData) => {
    const m = memberDetails?.[rowData?.Assignee];
    return (
      <Text
        variant={textVariantsEnum.Text_13}
        fontWeight={fontWeightEnum.Medium}
      >
        {m?.userName || m?.userEmailid || rowData?.Assignee || ''}
      </Text>
    );
  };

  const etaBodyTemplate = (rowData) => (
    <Text variant={textVariantsEnum.Text_13} fontWeight={fontWeightEnum.Medium}>
      {rowData?.ETA?.split('/')?.join('-')}
    </Text>
  );

  const indexBodyTemplate = (_, column) => (
    <Text variant={textVariantsEnum.Text_13} fontWeight={fontWeightEnum.Medium}>
      {column.rowIndex + 1}
    </Text>
  );

  const statusBodyTemplate = (rowData) => (
    <Tag value={rowData.Status} severity={STATUS_SEVERITY[rowData.Status]} />
  );

  const navigate = async (data) => {
    const projectData = await database
      .collection('companyDatabase')
      .doc(activeCompany.companyId)
      .collection('projects')
      .doc(data.ProjectID)
      .get();
    const projectName = projectData.data().project_name.split(' ').join('-');
    const newWindow = window.open(`task/${projectName}/${data.id}`, '_blank');
    newWindow['projectId'] = data.ProjectID;
  };

  const filterTaskList = (taskList, filters) =>
    taskList.filter((item) =>
      Object.entries(filters).every(([key, values]) => {
        if (!values.length) return true;

        if (key === 'searchTerm') {
          return (
            item?.Description?.toLowerCase().includes(values.toLowerCase()) ||
            item?.Title?.toLowerCase().includes(values.toLowerCase())
          );
        }
        if (key === 'ProjectID') {
          return values.some((v) => item[key] === v?.value);
        }
        return values.some((v) => item[key] === v?.value);
      })
    );

  useEffect(() => {
    if (!Array.isArray(initialTaskLists)) {
      setTaskLists([]);
      return;
    }
    setTaskLists(
      filterTaskList(initialTaskLists, {
        searchTerm,
        Assignee: assigneeFilter,
        Status: statusFilter,
        Priority: priorityFilter,
        ProjectID: selectedProjects,
      })
    );
  }, [
    assigneeFilter,
    statusFilter,
    priorityFilter,
    searchTerm,
    selectedProjects,
    dataStatus,
    initialTaskLists,
  ]);

  const fetchTasksFromProjects = async (projectIds) => {
    if (!projectIds?.length) {
      setTaskLists([]);
      setInitialTaskLists([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const snapshot =
        projectIds.length === 1
          ? await database
              .collection('taskDatabase')
              .where('ProjectID', '==', projectIds[0])
              .get()
          : await database
              .collection('taskDatabase')
              .where('ProjectID', 'in', projectIds)
              .get();

      const listsWithId = snapshot.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((task) => !task.Deleted);

      setInitialTaskLists(listsWithId);
    } catch (error) {
      console.error('[ProdaffTasksTab] fetch tasks error:', error);
      setTaskLists([]);
      setInitialTaskLists([]);
    } finally {
      setLoading(false);
      setDataStatus((prev) => !prev);
    }
  };

  const handleProjectChange = async (selected) => {
    setSelectedProjects(selected);
    if (selected.length === 0) {
      setTaskLists([]);
      setInitialTaskLists([]);
    } else {
      await fetchTasksFromProjects(selected.map((p) => p.value));
    }
  };

  const memberOptions = members.map((m) => ({
    value: m.userUID,
    label: m.userName,
  }));
  const projectOptions = Array.isArray(teams.projects)
    ? teams.projects
        .filter((p) => !p?.data?.deleted)
        .map((p) => ({ value: p.id, label: p.data.project_name }))
    : [];

  const toggleAddTask = useCallback(
    () => history.push(appRoutes().addTask),
    []
  );

  if (!isInitialDataLoaded) {
    return (
      <div
        className="w-100 d-flex justify-content-center align-items-center"
        style={{ minHeight: '60vh' }}
      >
        <div className="text-center">
          <PulseLoader color="#4f58bb" size={15} />
          <div className="mt-3 text-muted">Loading...</div>
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <Card className="bg-secondary shadow">
        <div className="text-center p-5">
          <div className="text-muted mb-3">
            No projects found. Please create some projects first.
          </div>
          <Button color="info" onClick={() => history.push('/project')}>
            Create Project
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <>
      <div className={styles.dropdownContainer}>
        <MultiSelectDropdown
          label="Project"
          options={projectOptions}
          value={selectedProjects}
          onChange={handleProjectChange}
        />
        <MultiSelectDropdown
          label="Assignee"
          options={memberOptions}
          value={assigneeFilter}
          onChange={setAssigneeFilter}
        />
        <MultiSelectDropdown
          label="Status"
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={setStatusFilter}
        />
        <MultiSelectDropdown
          label="Priority"
          options={PRIORITY_OPTIONS}
          value={priorityFilter}
          onChange={setPriorityFilter}
        />
      </div>

      <div className={styles.taskListContainer}>
        <div className={styles.taskListHeader}>
          <Button
            type="button"
            icon="pi pi-refresh"
            className="p-button-text col-1"
            onClick={refreshDataTable}
          />
          <div className={styles.searchContainer}>
            <InputComponent
              placeholder="Search Your Task List"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
            <ButtonComponent
              text="Add Task"
              onClick={toggleAddTask}
              variant={buttonVariants.primary}
              className={styles.addTaskButton}
            />
          </div>
        </div>

        {loading ? (
          <div className="w-100 d-flex justify-content-center p-4">
            <PulseLoader color="#4f58bb" />
          </div>
        ) : selectedProjects.length === 0 ? (
          <div className="text-center p-4 text-muted">
            Please select a project to view tasks.
          </div>
        ) : taskLists.length === 0 ? (
          <div className="text-center p-4">
            <div className="text-muted mb-3">No tasks found.</div>
            <ButtonComponent
              text="Add Task"
              onClick={toggleAddTask}
              variant={buttonVariants.primary}
            />
          </div>
        ) : (
          <DataTable
            className="prime-react-table"
            responsiveLayout="scroll"
            value={items}
            paginator
            paginatorClassName="prime-react-paginator"
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
            style={{ cursor: 'pointer' }}
            rows={50}
            rowsPerPageOptions={[10, 20, 50]}
            onRowClick={(e) => navigate(e?.data)}
          >
            <Column
              field="#"
              header="Sr.No."
              body={indexBodyTemplate}
              className="first-col td-start"
            />
            <Column
              field="Title"
              header="Title"
              className="second-col elipsis"
              sortable
            />
            <Column
              field="id"
              header="Task Id"
              className="forth-col td-start"
              style={{ minWidth: '130px' }}
            />
            <Column
              field="Status"
              header="Status"
              body={statusBodyTemplate}
              className="forth-col td-start"
              style={{ minWidth: '130px' }}
              sortable
            />
            <Column
              field="userName"
              header="Assignee"
              body={assigneeBodyTemplate}
              className="fifth-col td-start"
              sortable
            />
            <Column
              field="eta"
              header="ETA"
              body={etaBodyTemplate}
              className="fifth-col td-start"
              sortable
            />
            <Column
              field="Priority"
              header="Priority"
              className="first-col td-start"
              sortable
            />
          </DataTable>
        )}
      </div>
    </>
  );
};

export default ProdaffTasksTab;
